import {
	GROUP_COLORS,
	type GroupRow,
	type InformationRetrievalRow,
	type SummaryKey,
} from "../models/InformationRetrieval";

/**
 * Stand order the mill rolls in. `S1` (shear) and the two straightener stands
 * are excluded: production shows 17 columns, and the shear carries no R-Factor.
 * The new tracking writes `BilletStandData` rows for `S1` that the legacy one
 * never wrote, so filtering by name here is what keeps the two screens equal.
 * See docs/reports/information-retrieval/LEGACY-BEHAVIOUR.md §2.
 */
export const MILL_STANDS = [
	"H1",
	"V2",
	"H3",
	"HV4",
	"V5",
	"H6",
	"H7",
	"V8",
	"H9",
	"H10",
	"HV11",
	"H12",
	"H13",
	"HV14",
	"H15",
	"HV16",
	"H17",
] as const;

const groupLabel = (date: string, shift: string): string => {
	const cleanDate = date && !date.startsWith("1900-01-01") ? date : "";
	if (cleanDate && shift) return `${cleanDate} - ${shift}`;
	return cleanDate || shift || "All";
};

export const buildGroups = (rows: InformationRetrievalRow[]): GroupRow[] => {
	const groups = new Map<string, GroupRow>();

	for (const row of rows) {
		const key = `${row.Date}|${row.Shift}`;
		let group = groups.get(key);

		if (!group) {
			group = {
				key,
				label: groupLabel(row.Date, row.Shift),
				legendColor: GROUP_COLORS[groups.size % GROUP_COLORS.length],
				recipeName: row.RecipeName,
				stands: {},
				rows: [],
			};
			groups.set(key, group);
		}

		group.rows.push(row);

		if (!row.Stand) continue;

		group.stands[row.Stand] = {
			avg: row.RFactorAvg,
			target: row.RFactorTargetAvg,
			min: row.RFactorRecipeMinAvg,
			max: row.RFactorRecipeMaxAvg,
			active: row.Active === 1,
		};
	}

	return [...groups.values()];
};

/**
 * Red means "outside the recipe band". A stand the recipe does not cover has no
 * band, so there is nothing to be outside of.
 *
 * The legacy compares in JavaScript against possibly-null bounds, where
 * `1.0 > null` coerces to `1.0 > 0` and comes out true — so every stand without
 * a recipe paints red at exactly 1.000. On this product that hits HV11 and H17,
 * which the recipe deliberately leaves open (IsSelected = 0, tracking reports
 * Selected = 0, Working = 0 and speed 0.00): a stand that is not rolling reads
 * as one rolling far out of tolerance.
 *
 * Not reproduced. The cell already shows "--------" for the missing band in the
 * same grid, so painting the value red states the opposite of what the row says.
 */
export const isOutOfRange = (
	avg: number | null,
	min: number | null,
	max: number | null,
): boolean => {
	if (avg === null || avg === undefined) return false;
	if (min === null || min === undefined) return false;
	if (max === null || max === undefined) return false;
	return avg < min || avg > max;
};

const populationStdDeviation = (values: number[]): number => {
	if (values.length === 0) return 0;
	const mean = values.reduce((acc, v) => acc + v, 0) / values.length;
	const variance =
		values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / values.length;
	return Math.sqrt(variance);
};

export type SummaryValues = Record<
	SummaryKey,
	Record<string, number | null | undefined>
>;

/**
 * The aggregate grid, computed over the ticked groups only — and, inside those,
 * only over stand rows that carry an R-Factor sum, which is the legacy's own
 * guard. `undefined` marks a stand the selection never produced, rendered as
 * an inactive cell.
 */
export const buildSummary = (
	groups: GroupRow[],
	selectedKeys: string[],
	stands: string[],
): SummaryValues => {
	const summary: SummaryValues = {
		actual: {},
		target: {},
		maxActual: {},
		minActual: {},
		maxRecipe: {},
		minRecipe: {},
		percOutOfLimits: {},
		stdWithRecipe: {},
	};

	const selected = groups.filter((g) => selectedKeys.includes(g.key));

	for (const stand of stands) {
		let rfactor = 0;
		let target = 0;
		let min = 0;
		let max = 0;
		let recipeMin = 0;
		let recipeMax = 0;
		let countAvg = 0;
		let countBillets = 0;
		let countRecipe = 0;
		let outOfRangeCount = 0;
		let seen = false;
		const perGroupAverages: number[] = [];

		for (const group of selected) {
			const row = group.rows.find((r) => r.Stand === stand);
			if (!row || row.RFactorSum === null) continue;

			seen = true;
			rfactor += row.RFactorSum ?? 0;
			perGroupAverages.push(row.RFactorAvg ?? 0);
			target += row.RFactorTargetSum ?? 0;
			min += row.RFactorMinSum ?? 0;
			max += row.RFactorMaxSum ?? 0;
			recipeMin += row.RFactorRecipeMinSum ?? 0;
			recipeMax += row.RFactorRecipeMaxSum ?? 0;
			countAvg += row.RFactorCount ?? 0;
			countBillets += row.BilletsCount ?? 0;
			countRecipe += row.RecipeCount ?? row.BilletsCount ?? 0;

			// Legacy compares the running totals, not the per-group values.
			// Kept as-is so the percentage matches the old screen.
			if (rfactor < min || rfactor > max) outOfRangeCount++;
		}

		if (!seen) {
			for (const key of Object.keys(summary) as SummaryKey[]) {
				summary[key][stand] = undefined;
			}
			continue;
		}

		// Every row divides by the billets its own sum covers. The legacy divides the
		// recipe rows by BilletsCount while dividing Avg Actual by RFactorCount — the
		// same calculation with two different denominators, in one procedure.
		// Billets that left no row for a stand are absent from the sum but still count
		// in the divisor, so they behave as zeros. Measured on 2026-06-09, 3x3.5
		// channel: V5 holds 151 rows for 224 billets, and the legacy divisor drops
		// Recipe Actual from the recipe's 1.162 to 0.783 — below the chart's floor.
		// Dividing by RecipeCount reproduces the loaded recipe exactly.
		summary.actual[stand] = countAvg ? rfactor / countAvg : null;
		summary.target[stand] = countRecipe ? target / countRecipe : null;
		summary.maxActual[stand] = countAvg ? max / countAvg : null;
		summary.minActual[stand] = countAvg ? min / countAvg : null;
		summary.maxRecipe[stand] = countRecipe ? recipeMax / countRecipe : null;
		summary.minRecipe[stand] = countRecipe ? recipeMin / countRecipe : null;
		summary.percOutOfLimits[stand] = countBillets
			? (100 * outOfRangeCount) / countBillets
			: null;
		summary.stdWithRecipe[stand] = populationStdDeviation(perGroupAverages);
	}

	return summary;
};

/**
 * The legend line between the two grids. Empty names are kept rather than
 * discarded: the procedure blanks `RecipeName` when a group spans more than one
 * recipe, and the legacy screen counts that blank as a distinct value — a
 * selection that is entirely blank shows an empty legend, not "no recipes".
 */
export const buildRecipeLegend = (
	groups: GroupRow[],
	selectedKeys: string[],
): string => {
	const names = new Set(
		groups
			.filter((g) => selectedKeys.includes(g.key))
			.flatMap((g) => g.rows)
			.filter((r) => r.RFactorSum !== null)
			.map((r) => r.RecipeName ?? ""),
	);

	if (names.size === 1) return [...names][0];
	if (names.size > 1) return "There are multiple recipes on the resultset.";
	return "No recipes on the resultset.";
};

/** Aggregate series for the chart, on the same accumulate-then-divide basis. */
export const buildChartSeries = (
	groups: GroupRow[],
	selectedKeys: string[],
	stands: string[],
) => {
	const actual: (number | null)[] = [];
	const target: (number | null)[] = [];
	const minActual: (number | null)[] = [];
	const maxActual: (number | null)[] = [];
	const minRecipe: (number | null)[] = [];
	const maxRecipe: (number | null)[] = [];

	const selected = groups.filter((g) => selectedKeys.includes(g.key));

	for (const stand of stands) {
		let rfactor = 0;
		let countAvg = 0;
		let countBillets = 0;
		let countRecipe = 0;
		let seen = false;

		// Each series tracks its own presence. A stand with no recipe has NULL in
		// the recipe columns; treating that as 0 is what made the recipe lines
		// dive to the floor at exactly the stands the grid shows as "--------".
		// A series with nothing behind it must be a gap, not a zero.
		const sums: Record<string, { total: number; seen: boolean }> = {
			tgt: { total: 0, seen: false },
			mn: { total: 0, seen: false },
			mx: { total: 0, seen: false },
			rMin: { total: 0, seen: false },
			rMax: { total: 0, seen: false },
		};

		const add = (key: string, value: number | null) => {
			if (value === null || value === undefined) return;
			sums[key].total += value;
			sums[key].seen = true;
		};

		for (const group of selected) {
			const row = group.rows.find((r) => r.Stand === stand);
			if (!row || row.RFactorSum === null) continue;

			seen = true;
			rfactor += row.RFactorSum ?? 0;
			countAvg += row.RFactorCount ?? 0;
			countBillets += row.BilletsCount ?? 0;
			countRecipe += row.RecipeCount ?? row.BilletsCount ?? 0;

			add("tgt", row.RFactorTargetSum);
			add("mn", row.RFactorMinSum);
			add("mx", row.RFactorMaxSum);
			add("rMin", row.RFactorRecipeMinSum);
			add("rMax", row.RFactorRecipeMaxSum);
		}

		const avg = (key: string, denominator: number) =>
			seen && denominator && sums[key].seen
				? sums[key].total / denominator
				: null;

		// Denominators mirror buildSummary.
		actual.push(seen && countAvg ? rfactor / countAvg : null);
		target.push(avg("tgt", countRecipe));
		minActual.push(avg("mn", countAvg));
		maxActual.push(avg("mx", countAvg));
		minRecipe.push(avg("rMin", countRecipe));
		maxRecipe.push(avg("rMax", countRecipe));
	}

	return { actual, target, minActual, maxActual, minRecipe, maxRecipe };
};
